import React, { useState } from 'react';
import { Download } from 'lucide-react';
import { BusinessReportManager } from './reports/BusinessReportManager';

interface FileData {
  filename: string;
  mime_type: string;
  base64: string;
}

// Función para detectar si el contenido tiene formato binario
const isBinaryContent = (text: string): boolean => {
  // Comprueba si hay muchos caracteres no imprimibles
  const nonPrintable = text.replace(/[\x20-\x7E\r\n\t]/g, '');
  
  // Si más del 15% son caracteres no imprimibles, probablemente es binario
  if (nonPrintable.length > text.length * 0.15) {
    return true;
  }
  
  // Detectar patrones específicos de formatos binarios
  const binaryPatterns = [
    /^%PDF/, // PDF
    /PK\x03\x04/, // ZIP/DOCX/XLSX
    /^\xD0\xCF\x11\xE0/, // Compound File Binary (DOC)
    /^\x50\x4B\x03\x04/, // ZIP signature
    /^\x25\x50\x44\x46/, // %PDF
    /obj\s*<</,
    /endobj/,
    /stream/,
    /endstream/,
    /xref/,
    /trailer/
  ];
  
  return binaryPatterns.some(pattern => pattern.test(text));
};

export const renderMessageContent = (content: string) => {
  if (!content) return null;

  try {
    // Verificar si el contenido parece contener datos de un archivo de OpenAI
    const containsOpenAIFileData = () => {
      // Patrones comunes en la salida de OpenAI
      const openAIPatterns = [
        /"filename":/i,
        /"mime_type":/i,
        /"base64":/i,
        /```json/i,
        /Content-Type:/i,
        /Content-Disposition:/i
      ];
      
      return openAIPatterns.some(pattern => pattern.test(content));
    };
    
    // Extraer datos JSON del contenido (maneja diferentes formatos y errores)
    const extractJSONData = () => {
      try {
        // Intentar primero con bloques de código
        const jsonMatch = content.match(/```(?:json)?\s*([\s\S]+?)\s*```/);
        if (jsonMatch) {
          try {
            return JSON.parse(jsonMatch[1].trim());
          } catch (jsonError) {
            console.log("Error al parsear JSON en bloque de código, intentando reparación:", jsonError);
            
            // Intento de reparación del JSON
            let jsonStr = jsonMatch[1].trim();
            // Reemplazar comillas no válidas
            jsonStr = jsonStr.replace(/[']/g, '"');
            // Añadir comillas a las claves sin comillas
            jsonStr = jsonStr.replace(/(\s*?{\s*?|\s*?,\s*?)(['"])?([a-zA-Z0-9_]+)(['"])?:/g, '$1"$3":');
            // Limpiar JSON malformado
            jsonStr = jsonStr.replace(/,\s*}/g, '}');
            
            try {
              return JSON.parse(jsonStr);
            } catch (repairError) {
              console.log("Error al reparar JSON:", repairError);
              // Si falla, intentar extraer manualmente
              const filenameMatch = jsonStr.match(/"filename"\s*:\s*"([^"]+)"/);
              const mimeTypeMatch = jsonStr.match(/"mime_type"\s*:\s*"([^"]+)"/);
              const base64Match = jsonStr.match(/"base64"\s*:\s*"([^"]+)"/);
              
              if (filenameMatch && base64Match) {
                return {
                  filename: filenameMatch[1],
                  mime_type: mimeTypeMatch ? mimeTypeMatch[1] : "application/octet-stream",
                  base64: base64Match[1]
                };
              }
            }
          }
        }
        
        // Buscar patrones de JSON con { } o [ ]
        try {
          const jsonPattern = /(\{[\s\S]*\}|\[[\s\S]*\])/;
          const jsonStringMatch = content.match(jsonPattern);
          if (jsonStringMatch) {
            return JSON.parse(jsonStringMatch[1].trim());
          }
        } catch (e) {
          console.log("Error al parsear JSON con patrón:", e);
        }
        
        // Si llega aquí, buscar clave-valor explícitamente
        try {
          // Extraer partes clave del JSON usando expresiones regulares
          const filenameMatch = content.match(/"filename"\s*:\s*"([^"]+)"/);
          const mimeTypeMatch = content.match(/"mime_type"\s*:\s*"([^"]+)"/);
          
          // Buscar base64 de manera más flexible
          const base64Pattern = /"base64"\s*:\s*"([^"]{20,})(?:"|\s|$)/;
          const base64Match = content.match(base64Pattern);
          
          if (filenameMatch && base64Match) {
            return {
              filename: filenameMatch[1],
              mime_type: mimeTypeMatch ? mimeTypeMatch[1] : "application/octet-stream",
              base64: base64Match[1]
            };
          }
        } catch (e) {
          console.log("Error al extraer partes del JSON:", e);
        }
        
        // Si todo falla, intentar parsear todo el contenido
        try {
          return JSON.parse(content.trim());
        } catch (e) {
          console.log("Error al parsear contenido completo como JSON:", e);
          throw new Error("No se pudo extraer datos JSON válidos del contenido");
        }
      } catch (error) {
        console.error("Error general al extraer JSON:", error);
        throw error;
      }
    };
    
    // Si parece contener datos de archivo, procesar
    if (containsOpenAIFileData()) {
      try {
        const jsonData = extractJSONData();
        const fileArray = Array.isArray(jsonData) ? jsonData : [jsonData];
        
        if (fileArray.some(f => f.filename && (f.mime_type || f.type) && (f.base64 || f.content || f.data))) {
          // Normalizar formato a nuestro estándar
          const normalizedFiles = fileArray.map(f => ({
            filename: f.filename || f.name || 'file.bin',
            mime_type: f.mime_type || f.type || 'application/octet-stream',
            base64: f.base64 || f.content || f.data || ''
          }));
          
          // Solo mostrar los botones de descarga, sin mostrar el JSON
          return (
            <div className="flex flex-col space-y-2">
              {normalizedFiles.map((file, i) => (
                <div key={i} className="flex flex-col space-y-1">
                  <button
                    onClick={() => downloadFile(file)}
                    className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg transition-colors"
                  >
                    <Download className="w-5 h-5" />
                    <span>Descargar {file.filename}</span>
                  </button>
                  
                  {/* Opción de extracción y formateo para tipos de archivo compatibles */}
                  {(file.mime_type?.includes('word') || 
                    file.mime_type?.includes('document') || 
                    file.filename?.endsWith('.doc') || 
                    file.filename?.endsWith('.docx') ||
                    file.mime_type?.includes('pdf') ||
                    file.filename?.endsWith('.pdf') ||
                    file.mime_type?.includes('excel') ||
                    file.mime_type?.includes('spreadsheet') ||
                    file.filename?.endsWith('.xls') ||
                    file.filename?.endsWith('.xlsx')) ? (
                    <BusinessReportButton file={file} />
                  ) : null}
                </div>
              ))}
            </div>
          );
        }
      } catch (e) {
        console.error("Error extracting OpenAI file data:", e);
      }
      
      // Si detectamos que es contenido de archivos pero no pudimos procesarlo,
      // mostrar un mensaje genérico
      return <span>Contenido de archivo detectado - Use los botones de descarga si están disponibles</span>;
    }
  } catch (e) {
    console.error('Error processing content:', e);
  }

  // Si no es JSON de archivos o no pudimos procesarlo, renderizar como texto normal
  const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = content.split(new RegExp(`(${markdownLinkRegex.source}|${urlRegex.source})`, 'g'));

  return parts.map((part, index) => {
    if (!part) return null;

    const markdownMatch = part.match(/\[([^\]]+)\]\(([^)]+)\)/);
    if (markdownMatch) {
      const [_, label, url] = markdownMatch;
      return (
        <a
          key={index}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          {label}
        </a>
      );
    }

    if (urlRegex.test(part)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          {part}
        </a>
      );
    }

    return <span key={index}>{part}</span>;
  }).filter(Boolean);
};

export interface Message {
  id?: string;
  role: 'user' | 'assistant' | 'error' | 'system';
  content: string;
  created_at?: string;
  openai_message_id?: string;
  openai_thread_id?: string;
  file_ids?: string[];
  files?: string[];
}

interface MessageProps {
  message: Message;
}

// Business Report Button Component
const BusinessReportButton: React.FC<{ file: FileData }> = ({ file }) => {
  const [showReportManager, setShowReportManager] = useState(false);
  const [extractedText, setExtractedText] = useState<string | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);

  const handleExtractText = async () => {
    try {
      setIsExtracting(true);
      
      // Clean up base64 data
      let base64 = file.base64.trim();
      if (base64.includes('base64,')) {
        base64 = base64.split('base64,')[1];
      }
      
      // Decode base64
      let decodedContent;
      try {
        const cleanBase64 = base64.replace(/\s/g, '');
        decodedContent = atob(cleanBase64);
      } catch (e) {
        // Second attempt with more cleaning
        const fixedBase64 = base64.replace(/[^A-Za-z0-9+/=]/g, '');
        const remainder = fixedBase64.length % 4;
        const paddedBase64 = remainder > 0 ? 
          fixedBase64 + '='.repeat(4 - remainder) : fixedBase64;
        decodedContent = atob(paddedBase64);
      }
      
      // Extract readable text
      let processedText;
      if (isBinaryContent(decodedContent)) {
        processedText = extractReadableText(decodedContent, file.mime_type);
      } else {
        processedText = decodedContent;
      }
      
      setExtractedText(processedText);
      setShowReportManager(true);
    } catch (error) {
      console.error('Error extracting text:', error);
      alert('No se pudo extraer texto del documento. El archivo podría estar dañado o tener un formato no compatible.');
    } finally {
      setIsExtracting(false);
    }
  };

  return (
    <>
      <button
        onClick={handleExtractText}
        disabled={isExtracting}
        className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 bg-gray-50 hover:bg-gray-100 px-4 py-1 rounded-lg transition-colors text-sm"
      >
        {isExtracting ? (
          <span>Extrayendo texto...</span>
        ) : (
          <span>⚠️ Extraer y aplicar formato empresarial</span>
        )}
      </button>
      
      {showReportManager && extractedText && (
        <BusinessReportManager 
          content={extractedText}
          onClose={() => setShowReportManager(false)}
        />
      )}
    </>
  );
};

// Extract readable text from binary content
const extractReadableText = (content: string, mimeType: string): string => {
  try {
    // Remove non-printable characters
    let text = content.replace(/[^\x20-\x7E\r\n\t]/g, ' ');
    
    // Remove known binary structures
    text = text
      .replace(/%PDF[^]*?obj/g, '')
      .replace(/endobj[^]*?obj/g, '\n')
      .replace(/stream[^]*?endstream/g, '')
      .replace(/xref[^]*?trailer/g, '')
      .replace(/trailer[^]*?%%EOF/g, '')
      .replace(/PK[\x00-\x20]/g, '')
      .replace(/mso-application/g, '')
      .replace(/Content-Type:/g, '');
    
    // Clean up multiple spaces and empty lines
    text = text
      .replace(/\s+/g, ' ')
      .replace(/\n\s*\n/g, '\n\n')
      .trim();
    
    // If the result is very short or empty, return explanatory message
    if (text.length < 20) {
      if (mimeType.includes('pdf')) {
        return "No se pudo extraer texto legible de este PDF. Puede ser un PDF escaneado o con contenido protegido.";
      } else if (mimeType.includes('word') || mimeType.includes('document')) {
        return "No se pudo extraer texto legible de este documento Word. El formato puede ser incompatible o estar protegido.";
      } else if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) {
        return "No se pudo extraer texto legible de esta hoja de cálculo. El formato puede ser incompatible o estar protegido.";
      } else {
        return "No se pudo extraer texto legible de este archivo. El formato parece ser binario.";
      }
    }
    
    // Try to extract longer text blocks
    const textBlocks = text.match(/[A-Za-z0-9\s.,;:'\(\)\-"]{20,}/g);
    if (textBlocks && textBlocks.length > 0) {
      return textBlocks.join("\n\n");
    }
    
    return text;
  } catch (e) {
    console.error("Error al extraer texto legible:", e);
    return "Error al procesar el archivo. No se pudo extraer texto legible.";
  }
};

export const MessageBubble: React.FC<MessageProps> = ({ message }) => {
  const { role, content } = message;

  if (role === 'system') {
    return (
      <div className="bg-gray-100 px-4 py-2 rounded-full text-sm text-gray-600 shadow-sm">
        {content}
      </div>
    );
  }
  
  // Verificar si el contenido es un JSON con datos de archivo
  const isFileJSON = () => {
    try {
      // Buscar contenido JSON en diferentes formatos
      let jsonStr = content;
      const jsonMatch = content.match(/```json\s*([\s\S]+?)\s*```/);
      if (jsonMatch) {
        jsonStr = jsonMatch[1].trim();
      }
      
      // Intentar analizar
      if ((jsonStr.startsWith('{') && jsonStr.endsWith('}')) || 
          (jsonStr.startsWith('[') && jsonStr.endsWith(']'))) {
        const jsonData = JSON.parse(jsonStr);
        const fileArray = Array.isArray(jsonData) ? jsonData : [jsonData];
        
        return fileArray.every(f => f.filename && f.mime_type && f.base64);
      }
      return false;
    } catch (e) {
      return false;
    }
  };

  const hasFileData = isFileJSON();

  return (
    <div className="space-y-2">
      {!hasFileData && (
        <div
          className={`p-4 rounded-lg font-mono text-sm whitespace-pre-wrap shadow-sm ${
            role === 'user'
              ? 'bg-blue-500 text-white'
              : role === 'error'
              ? 'bg-red-50 text-red-700 border border-red-100'
              : 'bg-gray-50 text-gray-900 border border-gray-100'
          }`}
        >
          {renderMessageContent(content)}
        </div>
      )}
      
      {hasFileData && (
        <div className="p-4">
          {renderMessageContent(content)}
        </div>
      )}

      {message.files && message.files.length > 0 && (
        <div className="flex flex-col space-y-1">
          {message.files.map((fileName, index) => (
            <div 
              key={index}
              className={`text-xs px-3 py-1 rounded-full inline-flex items-center space-x-1 ${
                role === 'user' 
                  ? 'bg-blue-400 text-white' 
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              <Download className="w-3 h-3" />
              <span>{fileName}</span>
            </div>
          ))}
        </div>
      )}

      {message.file_ids && message.file_ids.length > 0 && (
        <div className="mt-1 space-y-1 text-sm">
          {message.file_ids.map((fileId) => (
            <a
              key={fileId}
              href={`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/download-file?file_id=${fileId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              📎 Descargar archivo ({fileId})
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

// Function to download file from OpenAI
const downloadFile = (fileData: FileData) => {
  try {
    // Verify that base64 is not undefined or null
    if (!fileData.base64) {
      throw new Error('Los datos base64 están vacíos');
    }

    let base64 = fileData.base64.trim();
    console.log("Procesando datos de OpenAI Code Interpreter");
    
    // Remove data URL prefixes
    if (base64.includes('base64,')) {
      base64 = base64.split('base64,')[1];
    } else if (base64.includes(',')) {
      base64 = base64.split(',')[1];
    }
    
    // Detect file type by extension and MIME type
    const isPdf = 
      fileData.mime_type?.includes('pdf') ||
      fileData.filename?.endsWith('.pdf');
      
    const isWordDocument = 
      fileData.mime_type?.includes('word') ||
      fileData.mime_type?.includes('document') ||
      fileData.filename?.endsWith('.doc') ||
      fileData.filename?.endsWith('.docx');
      
    const isSpreadsheet = 
      fileData.mime_type?.includes('excel') ||
      fileData.mime_type?.includes('spreadsheet') ||
      fileData.filename?.endsWith('.xls') ||
      fileData.filename?.endsWith('.xlsx');
    
    // For files that we know might be binary, offer text extraction
    if (isPdf || isWordDocument || isSpreadsheet) {
      try {
        // Decode the base64
        let decodedContent;
        try {
          // Clean and decode
          const cleanBase64 = base64.replace(/\s/g, '');
          decodedContent = atob(cleanBase64);
        } catch (e) {
          // Second attempt with more cleaning
          let fixedBase64 = base64.replace(/[^A-Za-z0-9+/=]/g, '');
          // Ensure padding is correct
          const remainder = fixedBase64.length % 4;
          const paddedBase64 = remainder > 0 ? 
            fixedBase64 + '='.repeat(4 - remainder) : fixedBase64;
          decodedContent = atob(paddedBase64);
        }
        
        // Verify if it really has binary format
        if (isBinaryContent(decodedContent)) {
          // Suggest text extraction for binary files
          const fileType = isPdf ? "PDF" : isWordDocument ? "Word" : "Excel";
          if (confirm(`El archivo ${fileType} parece tener formato binario. ¿Desea extraer y formatear el texto en lugar de intentar descargar el archivo original?`)) {
            // Show format dialog
            const processedText = extractReadableText(decodedContent, fileData.mime_type);
            
            // Create a BusinessReportManager component instance
            const reportManager = document.createElement('div');
            reportManager.id = 'temp-report-manager';
            document.body.appendChild(reportManager);
            
            // Render the BusinessReportManager
            const React = require('react');
            const ReactDOM = require('react-dom');
            const BusinessReportManager = require('./reports/BusinessReportManager').BusinessReportManager;
            
            ReactDOM.render(
              React.createElement(BusinessReportManager, {
                content: processedText,
                onClose: () => {
                  ReactDOM.unmountComponentAtNode(reportManager);
                  document.body.removeChild(reportManager);
                }
              }),
              reportManager
            );
            
            return;
          }
        }
      } catch (error) {
        console.error("Error al analizar contenido binario:", error);
        // Continue with normal download if analysis fails
      }
    }
    
    // Continue with normal download process if user didn't choose extraction
    // Standard cleaning steps
    base64 = base64.replace(/[\r\n\t\f\v ]/g, '');
    base64 = base64.replace(/[^\x00-\x7F]/g, '');
    base64 = base64.replace(/[^A-Za-z0-9+/=]/g, '');
    
    // Fix padding
    base64 = base64.replace(/=/g, '');
    const remainder = base64.length % 4;
    if (remainder > 0) {
      base64 += '='.repeat(4 - remainder);
    }
    
    // Try to decode with error handling
    let binaryData;
    
    try {
      binaryData = atob(base64);
    } catch (error) {
      console.error("Error en la decodificación de OpenAI:", error);
      
      // Second attempt with more aggressive cleaning
      try {
        base64 = base64.replace(/[^A-Za-z0-9]/g, '');
        while (base64.length % 4 !== 0) {
          base64 += 'A';
        }
        binaryData = atob(base64);
      } catch (error2) {
        console.error("Falló segundo intento de decodificación:", error2);
        
       // Partial decoding as a last resort
        try {
          const safeLength = Math.floor(base64.length / 4) * 4;
          const safeBase64 = base64.substring(0, safeLength);
          
          if (safeLength > 0) {
            binaryData = atob(safeBase64);
            console.warn("Decodificación parcial exitosa con", safeLength, "caracteres");
          } else {
            throw new Error("No se pudo decodificar los datos generados por el asistente de OpenAI");
          }
        } catch (error3) {
          console.error("Todos los intentos de decodificación fallaron");
          
          if (fileData.mime_type === 'application/pdf') {
            // Basic valid PDF as a last resort
            binaryData = atob('JVBERi0xLjAKMSAwIG9iago8PC9UeXBlL0NhdGFsb2cvUGFnZXMgMiAwIFI+PgplbmRvYmoKMiAwIG9iago8PC9UeXBlL1BhZ2VzL0tpZHNbMyAwIFJdL0NvdW50IDE+PgplbmRvYmoKMyAwIG9iago8PC9UeXBlL1BhZ2UvTWVkaWFCb3ggWzAgMCAzIDNdPj4KZW5kb2JqCnhyZWYKMCA0CjAwMDAwMDAwMDAgNjU1MzUgZgowMDAwMDAwMDEwIDAwMDAwIG4KMDAwMDAwMDA1MyAwMDAwMCBuCjAwMDAwMDAxMDIgMDAwMDAgbgp0cmFpbGVyCjw8L1NpemUgNC9Sb290IDEgMCBSPj4Kc3RhcnR4cmVmCjE0OQolRU9G');
            console.warn("Generando PDF vacío como último recurso");
          } else {
            // Throw error if not a PDF and can't be decoded
            throw new Error("No hay suficientes caracteres para decodificar");
          }
        }
      }
    }
    
    // Create blob and download
    const bytes = new Uint8Array(binaryData.length);
    for (let i = 0; i < binaryData.length; i++) {
      bytes[i] = binaryData.charCodeAt(i);
    }
    
    // Use original mime type or fallback to application/octet-stream
    const mimeType = fileData.mime_type || 'application/octet-stream';
    const blob = new Blob([bytes], { type: mimeType });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileData.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log("Archivo de OpenAI descargado exitosamente");
  } catch (error) {
    console.error('Error downloading file from OpenAI:', error);
    alert(`Error al descargar el archivo generado por el asistente de OpenAI: ${error instanceof Error ? error.message : 'Error desconocido'}. 
    Si el problema persiste, intente solicitarle al asistente que genere el archivo en un formato diferente.`);
  }
};

// Export interfaces for use in other components
export interface OpenAIAssistant {
  id: string;
  name: string;
  description?: string;
  model: string;
  instructions?: string;
}

export interface APIConfig {
  id: string;
  nombre: string;
  api_key: string;
  organization_id: string | null;
  entorno: string;
  estado: string;
}